import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Compass, Warning, ArrowLeft } from "@phosphor-icons/react";
import TopBar from "../components/TopBar";
import RoadmapListPane from "../components/RoadmapListPane";
import RoadmapDetailPane from "../components/RoadmapDetailPane";
import RoadmapDashboardOverview from "../components/RoadmapDashboardOverview";
import RoadmapFiltersBarV2 from "../components/RoadmapFiltersBarV2";
import {
  unifyRoadmapItems,
  applyUnifiedFilters,
  DEFAULT_FILTERS,
} from "../lib/unifyRoadmapItems";
import { buildXrefLookup } from "../lib/xrefText";
import { XrefProvider } from "../lib/XrefContext";
import {
  fetchState,
  fetchPromisesByAudience,
  fetchLedger,
  fetchWorkbooks,
  fetchFederation,
  fetchDriftRisk,
  fetchPerAppRoadmaps,
  fetchOwnerDecisions,
  fetchPicksByWorkbook,
  fetchViewAuditByItem,
  fetchWorkAuditByItem,
  fetchCycleInbox,
} from "../lib/api";

/**
 * RoadmapPage — Phase 12E master-detail layout (cycle-5c+25-fix8).
 *
 * Owner direction:
 *   "one big list and i can use the filters at top to break it down;
 *    separate sections down the page make it hard to navigate and prioritise"
 *   + "a list of all roadmap items on the left, that when clicked open up
 *      the page on the right so not to go to new tab"
 *
 * Layout:
 *   - Top: TopBar + State summary collapses into the dashboard overview
 *   - Sticky filter bar (Search · Status · Type · Tier · Agent · Time · Waiting · Sort)
 *   - Two-column grid below (collapses to single-column < 1024px):
 *       Left:  RoadmapListPane (filtered unified items, click to select)
 *       Right: RoadmapDetailPane(selectedItem) OR RoadmapDashboardOverview if none
 *
 * URL state: ?item=<type>:<id> persists selection across reloads.
 */

export default function RoadmapPage() {
  const [state, setState] = useState(null);
  const [promises, setPromises] = useState({ owner: [], agent: [], both: [], none: [] });
  const [ledger, setLedger] = useState([]);
  const [workbooks, setWorkbooks] = useState([]);
  const [federation, setFederation] = useState(null);
  const [drift, setDrift] = useState(null);
  const [perAppRoadmaps, setPerAppRoadmaps] = useState(null);
  const [ownerDecisions, setOwnerDecisions] = useState([]);
  const [workbookAcksByKey, setWorkbookAcksByKey] = useState({});
  // cycle-5c+25-fix20 (Phase 14 / Batch 3): staleness signal sources.
  // workAuditByItem is canon-derived (latest decision/pick-ack timestamp
  // per item); viewAuditByItem is Mongo-stored explicit "I worked on this"
  // touches. Combined in unifyRoadmapItems → days_since_touch + tier.
  const [viewAuditByItem, setViewAuditByItem] = useState({});
  const [workAuditByItem, setWorkAuditByItem] = useState({});
  // cycle-5c+25-fix21 (ADR-MP-005): MP cycle-inbox payload — "new Owner
  // activity since MP last acknowledged". Banner renders when total_new > 0.
  const [cycleInbox, setCycleInbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("item") || null;

  // Narrow-screen single-column toggle: show list by default; clicking an
  // item flips to detail with a back button. On wide screens both visible.
  const [narrowView, setNarrowView] = useState(selectedId ? "detail" : "list");

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [stateData, prData, ldData, wbData, fedData, driftData, perAppData, decisionsData, picksByWb, viewAudit, workAudit, inbox] = await Promise.all([
        fetchState(),
        fetchPromisesByAudience(),
        fetchLedger(20),
        fetchWorkbooks(),
        fetchFederation(),
        fetchDriftRisk(),
        fetchPerAppRoadmaps(),
        fetchOwnerDecisions(true).catch(() => []),
        fetchPicksByWorkbook().catch(() => ({})),
        fetchViewAuditByItem().catch(() => ({})),
        fetchWorkAuditByItem().catch(() => ({})),
        fetchCycleInbox().catch(() => null),
      ]);
      setState(stateData);
      setPromises(prData);
      setLedger(ldData);
      setWorkbooks(wbData);
      setFederation(fedData);
      setDrift(driftData);
      setPerAppRoadmaps(perAppData);
      setOwnerDecisions(decisionsData || []);
      setWorkbookAcksByKey(picksByWb || {});
      setViewAuditByItem(viewAudit || {});
      setWorkAuditByItem(workAudit || {});
      setCycleInbox(inbox);
    } catch (e) {
      console.error("Failed to load canon", e);
      setError(e.message || "Failed to load canon");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Build a lookup map of latest decision per "<type>:<id>" key.
  const decisionsByKey = useMemo(() => {
    const map = {};
    for (const d of ownerDecisions) {
      map[`${d.item_type}:${d.item_id}`] = d;
    }
    return map;
  }, [ownerDecisions]);

  // Unify every source-type into one flat array.
  const allItems = useMemo(
    () =>
      unifyRoadmapItems({
        promises,
        workbooks,
        drift,
        federation,
        perAppRoadmaps,
        ledger,
        decisionsByKey,
        workbookAcksByKey,
        viewAuditByItem,
        workAuditByItem,
      }),
    [promises, workbooks, drift, federation, perAppRoadmaps, ledger, decisionsByKey, workbookAcksByKey, viewAuditByItem, workAuditByItem],
  );

  // cycle-5c+25-fix25: build the cross-reference lookup once per items
  // snapshot so <XrefText> can autolink [Cn] + MP_INBOX/<file>.md tokens
  // anywhere in the tree without prop-drilling.
  const xrefLookup = useMemo(() => buildXrefLookup(allItems), [allItems]);

  // Apply filters.
  const filteredItems = useMemo(
    () => applyUnifiedFilters(allItems, filters),
    [allItems, filters],
  );

  // Apply sort. mp_rec is the default (already pre-sorted in unifier).
  const sortedItems = useMemo(() => {
    if (filters.sort === "tier") {
      const tierOrder = { P0: 0, P1: 1, P2: 2, P3: 3, P4: 4, P5: 5, current: 0, next: 2, future: 4 };
      return [...filteredItems].sort((a, b) => {
        const ta = tierOrder[a.tier] ?? 99;
        const tb = tierOrder[b.tier] ?? 99;
        if (ta !== tb) return ta - tb;
        return a.mp_rec_rank - b.mp_rec_rank;
      });
    }
    if (filters.sort === "recent") {
      return [...filteredItems].sort((a, b) => {
        const da = a.raw?.last_decided_at || a.raw?.submitted_at || a.raw?.date || "";
        const db = b.raw?.last_decided_at || b.raw?.submitted_at || b.raw?.date || "";
        return db.localeCompare(da);
      });
    }
    if (filters.sort === "stale") {
      // cycle-5c+25-fix20 (Phase 14 / Batch 3): stale-first sort. Items
      // with the LARGEST `days_since_touch` float to the top. Never-touched
      // items (Infinity) come first, then progressively-fresher items.
      // Owner can pair this with the stale_min_days filter to surface
      // exactly "what I've been ignoring."
      return [...filteredItems].sort((a, b) => {
        const da = a.days_since_touch ?? -Infinity;
        const db = b.days_since_touch ?? -Infinity;
        if (db !== da) return db - da;
        // Tie-break: owner_actionable first, then mp_rec_rank ascending.
        const aOA = a.owner_actionable ? 0 : 1;
        const bOA = b.owner_actionable ? 0 : 1;
        if (aOA !== bOA) return aOA - bOA;
        return (a.mp_rec_rank ?? 999) - (b.mp_rec_rank ?? 999);
      });
    }
    return filteredItems; // mp_rec is pre-sorted
  }, [filteredItems, filters.sort]);

  // Resolve selected item ONLY from the visible filtered list. If the URL
  // points at an item that's been filtered out (decided / closed / filtered
  // by Status/Type/etc.), `selectedItem` resolves to null and the right pane
  // falls back to the dashboard overview. cycle-5c+25-fix11 per Owner —
  // "you have lost your right side initial stats info. it shows whatever
  //  was the last chosen item, decided or not".
  const selectedItem = useMemo(
    () => sortedItems.find((it) => it.id === selectedId) || null,
    [sortedItems, selectedId],
  );

  // If the URL has a stale ?item=... pointing at a now-hidden card, clear
  // it so browser back/forward + bookmarks stay consistent with what the
  // user actually sees on the page.
  useEffect(() => {
    if (selectedId && !selectedItem && !loading) {
      setSearchParams({});
      setNarrowView("list");
    }
  }, [selectedId, selectedItem, loading, setSearchParams]);

  const handleSelect = useCallback((id) => {
    if (id) {
      setSearchParams({ item: id });
      setNarrowView("detail");
    } else {
      setSearchParams({});
      setNarrowView("list");
    }
  }, [setSearchParams]);

  const handleBackToList = useCallback(() => {
    setNarrowView("list");
  }, []);

  const handleDecisionSubmitted = useCallback(async () => {
    try {
      const [fresh, freshAcks, freshWorkAudit] = await Promise.all([
        fetchOwnerDecisions(true),
        fetchPicksByWorkbook().catch(() => ({})),
        // cycle-5c+25-fix20: a fresh decision also changes the canonical
        // last_worked_at, so refetch the work-audit map to keep the
        // staleness tier in sync without a full reload.
        fetchWorkAuditByItem().catch(() => ({})),
      ]);
      setOwnerDecisions(fresh || []);
      setWorkbookAcksByKey(freshAcks || {});
      setWorkAuditByItem(freshWorkAudit || {});
      // After a successful decision commit, the card is now `status: "decided"`
      // and drops out of the default "Open" filter. Clear the URL so the right
      // pane returns to the dashboard overview instead of pointing at a card
      // that's no longer in the visible list (cycle-5c+25-fix10).
      // cycle-5c+25-fix13: same behaviour for workbook pick submissions —
      // refetch /api/picks/by-workbook so submitted workbooks auto-hide
      // alongside decided per-card decisions.
      setSearchParams({});
      setNarrowView("list");
    } catch (e) {
      console.error("Failed to refetch decisions", e);
    }
  }, [setSearchParams]);

  // cycle-5c+25-fix20 (Phase 14 / Batch 3): refresh view-audit map after
  // an explicit "I worked on this" touch so the staleness dot flips
  // green immediately without requiring a full page reload.
  const handleViewAuditTouched = useCallback(async () => {
    try {
      const fresh = await fetchViewAuditByItem();
      setViewAuditByItem(fresh || {});
    } catch (e) {
      console.error("Failed to refetch view audit", e);
    }
  }, []);

  // cycle-5c+25-fix21 (ADR-MP-005): after Owner clicks "Mark MP caught up"
  // the banner's POST /api/mp/cycle-acknowledge has bumped the marker.
  // Refetch the inbox so the banner empties + flips out of view (it now
  // sees 0 new since the just-bumped marker).
  const handleCycleAcknowledged = useCallback(async () => {
    try {
      const fresh = await fetchCycleInbox();
      setCycleInbox(fresh);
    } catch (e) {
      console.error("Failed to refetch cycle inbox", e);
    }
  }, []);

  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([k, v]) => v !== DEFAULT_FILTERS[k]);
  }, [filters]);

  // cycle-5c+25-fix16: Owner pick — "make the 'roadmap' title at top like
  // a home button, that resets page". The home action clears the deep-link
  // (so the right pane returns to the dashboard overview), resets all
  // filters to their defaults, and snaps the mobile narrow view back to
  // the list lane + scrolls to top.
  // cycle-5c+25-fix17: Owner follow-up — also re-scroll the LIST pane
  // scroller (data-testid="roadmap-list-scroll") to top, not just window.
  const handleHome = useCallback(() => {
    setSearchParams({});
    setFilters(DEFAULT_FILTERS);
    setNarrowView("list");
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      const listScroller = document.querySelector('[data-testid="roadmap-list-scroll"]');
      if (listScroller) {
        listScroller.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [setSearchParams]);

  return (
    <XrefProvider value={xrefLookup}>
    <div className="min-h-screen" style={{ background: "hsl(var(--background))" }}>
      <TopBar onRefresh={loadAll} refreshing={refreshing} onHome={handleHome} />

      <main className="mx-auto max-w-[1600px] px-3 sm:px-4 lg:px-6 py-3 sm:py-6">
        <div className="flex items-center gap-3 mb-4">
          <Compass size={20} style={{ color: "hsl(var(--primary))" }} />
          <h1 className="text-xl font-semibold" style={{ color: "hsl(var(--foreground))" }}>
            Roadmap
          </h1>
          <span
            className="text-mono ml-1"
            style={{ fontSize: "0.7rem", color: "hsl(var(--muted-foreground))" }}
          >
            — Master Planner · {allItems.length} items in canon
          </span>
        </div>

        {error && (
          <div
            className="surface-card mb-4 p-4 flex items-start gap-3"
            style={{
              borderColor: "hsl(var(--status-critical) / 0.5)",
              background: "hsl(var(--status-critical) / 0.05)",
            }}
            data-testid="mp-error-banner"
          >
            <Warning size={18} style={{ color: "hsl(var(--status-critical))" }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: "hsl(var(--status-critical))" }}>
                Failed to read canon
              </p>
              <p className="text-xs mt-1" style={{ color: "hsl(var(--muted-foreground))" }}>
                {error}
              </p>
            </div>
          </div>
        )}

        <RoadmapFiltersBarV2 filters={filters} setFilters={setFilters} />

        {/* Wide-screen: two columns. Narrow-screen: one column with toggle. */}
        <div className="lg:grid lg:grid-cols-[420px_1fr] lg:gap-6">
          {/* LIST column — hidden on narrow when in detail view */}
          <div className={`${narrowView === "detail" ? "hidden lg:block" : "block"} mb-4 lg:mb-0`}>
            {loading ? (
              <div className="surface-card animate-pulse h-96" />
            ) : (
              <RoadmapListPane
                items={sortedItems}
                totalCount={allItems.length}
                selectedId={selectedId}
                onSelect={handleSelect}
                hasActiveFilters={hasActiveFilters}
                onClearFilters={() => setFilters(DEFAULT_FILTERS)}
              />
            )}
          </div>

          {/* DETAIL column — hidden on narrow when in list view */}
          <div className={`${narrowView === "list" && selectedId ? "hidden lg:block" : "block"}`}>
            {selectedItem && (
              <button
                type="button"
                onClick={handleBackToList}
                className="inline-flex lg:hidden items-center gap-1.5 mb-3 rounded-md border px-3 py-1.5 text-sm hover:border-[hsl(var(--primary))]"
                style={{
                  borderColor: "hsl(var(--border))",
                  color: "hsl(var(--foreground))",
                  background: "hsl(var(--background))",
                }}
                data-testid="roadmap-back-to-list"
              >
                <ArrowLeft size={13} />
                Back to list
              </button>
            )}
            {loading ? (
              <div className="surface-card animate-pulse h-96" />
            ) : selectedItem ? (
              <RoadmapDetailPane
                item={selectedItem}
                allItems={allItems}
                onDecisionSubmitted={handleDecisionSubmitted}
                onViewAuditTouched={handleViewAuditTouched}
              />
            ) : (
              <RoadmapDashboardOverview
                state={state}
                items={sortedItems}
                totalCount={allItems.length}
                allItems={allItems}
                onSelect={handleSelect}
                cycleInbox={cycleInbox}
                onCycleAcknowledged={handleCycleAcknowledged}
              />
            )}
          </div>
        </div>

        <footer
          className="mt-12 pt-6 border-t text-center"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <p
            className="text-mono"
            style={{ fontSize: "0.65rem", color: "hsl(var(--muted-foreground))" }}
          >
            Live view of the shared canon · refreshes on demand · Phase 12E master-detail layout
          </p>
        </footer>
      </main>
    </div>
    </XrefProvider>
  );
}
